import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency, formatPercent } from "@/lib/utils";
import {
  TrendingUp, TrendingDown, Download, RefreshCw, BarChart3,
  ArrowUpRight, ArrowDownRight, Minus, ChevronDown, ChevronRight
} from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar, Legend, ReferenceLine,
  ComposedChart
} from "recharts";
import QuarterDetailPanel from "./QuarterDetailPanel";

const COLORS = {
  sky: "#0ea5e9",
  emerald: "#10b981",
  rose: "#f43f5e",
  amber: "#f59e0b",
  violet: "#8b5cf6",
  slate: "#64748b",
};

const TEAM_COLORS = [
  "#0ea5e9", "#f43f5e", "#10b981", "#f59e0b",
  "#8b5cf6", "#06b6d4", "#ec4899", "#14b8a6",
];

export default function PerformanceHistory({ team, game }) {
  const { t } = useTranslation();
  const [history, setHistory] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("cumulative_profit");
  const [chartType, setChartType] = useState("area");

  useEffect(() => {
    if (team?.id && game?.id) fetchData();
  }, [team?.id, game?.id]);

  const fetchData = async () => {
    try {
      const [historyRes, comparisonRes] = await Promise.all([
        axios.get(`${API}/teams/${team.id}/history`),
        axios.get(`${API}/games/${game.id}/comparison-history`)
      ]);
      setHistory(historyRes.data);
      setComparison(comparisonRes.data);
    } catch (err) { console.error("PerformanceHistory:", err.message); } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/teams/${team.id}/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${team.airline_code}_performance.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) { console.error("PerformanceHistory:", err.message); }
  };

  const handleExportJSON = async () => {
    try {
      const response = await axios.get(`${API}/teams/${team.id}/export/json`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${team.airline_code}_full_export.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) { console.error("PerformanceHistory:", err.message); }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-64 bg-slate-200 rounded-lg animate-pulse" />
        <div className="h-48 bg-slate-200 rounded-lg animate-pulse" />
      </div>
    );
  }

  const metrics = [
    { value: "cumulative_profit", label: t("dashboard.cumulativeProfit"), format: "currency", color: COLORS.emerald },
    { value: "stock_price", label: t("dashboard.stockPrice"), format: "currency", color: COLORS.sky },
    { value: "net_profit", label: t("dashboard.netProfit"), format: "currency", color: COLORS.amber },
    { value: "revenue", label: t("dashboard.revenue"), format: "currency", color: COLORS.violet },
    { value: "load_factor", label: t("dashboard.loadFactor"), format: "percent", color: COLORS.rose },
    { value: "quality_rating", label: t("dashboard.qualityRating"), format: "number", color: COLORS.sky },
  ];

  const selectedMetric = metrics.find(m => m.value === metric);
  const historyData = (history?.history || []).map(h => ({ ...h, name: `Q${h.quarter}` }));

  // Compute trend KPIs
  const latest = historyData[historyData.length - 1];
  const previous = historyData.length > 1 ? historyData[historyData.length - 2] : null;

  return (
    <div className="space-y-6" data-testid="performance-history">
      {/* Trend KPI Cards */}
      {previous && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <TrendCard label={t("dashboard.revenue")} current={latest?.revenue} previous={previous?.revenue} format="currency" />
          <TrendCard label={t("dashboard.netProfit")} current={latest?.net_profit} previous={previous?.net_profit} format="currency" />
          <TrendCard label={t("dashboard.stockPrice")} current={latest?.stock_price} previous={previous?.stock_price} format="currency" />
          <TrendCard label={t("dashboard.loadFactor")} current={latest?.load_factor} previous={previous?.load_factor} format="percent" />
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-heading font-semibold text-slate-900">{t("history.title")}</h3>
          <p className="text-sm text-slate-500">{t("history.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-44" data-testid="metric-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {metrics.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={chartType} onValueChange={setChartType}>
            <SelectTrigger className="w-28" data-testid="chart-type-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="area">{t("history.area")}</SelectItem>
              <SelectItem value="line">{t("history.line")}</SelectItem>
              <SelectItem value="bar">{t("history.bar")}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main Chart */}
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle>{selectedMetric?.label} Over Time</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {historyData.length > 1 ? (
            <div className="h-72" data-testid="main-chart">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === "area" ? (
                  <AreaChart data={historyData}>
                    <defs>
                      <linearGradient id="colorMetric" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={selectedMetric.color} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={selectedMetric.color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => selectedMetric.format === "currency" ? `$${(v / 1000).toFixed(0)}k` : `${v.toFixed(0)}`} />
                    <Tooltip content={<CustomTooltip format={selectedMetric.format} />} />
                    <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                    <Area type="monotone" dataKey={metric} stroke={selectedMetric.color} strokeWidth={2.5} fill="url(#colorMetric)" dot={{ r: 4, fill: selectedMetric.color }} activeDot={{ r: 6 }} />
                  </AreaChart>
                ) : chartType === "line" ? (
                  <LineChart data={historyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => selectedMetric.format === "currency" ? `$${(v / 1000).toFixed(0)}k` : `${v.toFixed(0)}`} />
                    <Tooltip content={<CustomTooltip format={selectedMetric.format} />} />
                    <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey={metric} stroke={selectedMetric.color} strokeWidth={2.5} dot={{ r: 4, fill: selectedMetric.color }} activeDot={{ r: 6 }} />
                  </LineChart>
                ) : (
                  <BarChart data={historyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => selectedMetric.format === "currency" ? `$${(v / 1000).toFixed(0)}k` : `${v.toFixed(0)}`} />
                    <Tooltip content={<CustomTooltip format={selectedMetric.format} />} />
                    <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                    <Bar dataKey={metric} fill={selectedMetric.color} radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72 flex items-center justify-center text-slate-500">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Not enough data yet. Complete more quarters to see trends.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue vs Expenses Composed Chart */}
      {historyData.length > 1 && (
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("history.revVsExp")}</CardTitle>
            <CardDescription>{t("history.financialBreakdown")}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="h-72" data-testid="revenue-expenses-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={historyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip format="currency" />} />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill={COLORS.emerald} radius={[4, 4, 0, 0]} opacity={0.8} />
                  <Bar dataKey="expenses" name="Expenses" fill={COLORS.rose} radius={[4, 4, 0, 0]} opacity={0.8} />
                  <Line type="monotone" dataKey="net_profit" name="Net Profit" stroke={COLORS.sky} strokeWidth={2.5} dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Competitor Comparison */}
      {comparison && comparison.teams?.length > 1 && (
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("history.competitorComparison")}</CardTitle>
            <CardDescription>{t("history.yourPerformanceVs")}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <ComparisonLineChart
              data={comparison.teams}
              currentTeamId={team.id}
              metric={metric}
              metricLabel={selectedMetric?.label}
              format={selectedMetric?.format}
            />
          </CardContent>
        </Card>
      )}

      {/* Quarter Details Table */}
      <QuarterDetailsTable
        historyData={historyData}
        teamId={team.id}
        t={t}
        handleExportCSV={handleExportCSV}
        handleExportJSON={handleExportJSON}
      />
    </div>
  );
}

function CustomTooltip({ active, payload, label, format }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg border border-slate-200 rounded-lg px-4 py-3">
      <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: p.color || p.stroke }} />
          <span className="text-sm text-slate-600">{p.name || p.dataKey}:</span>
          <span className="text-sm font-mono font-semibold text-slate-900">
            {format === "currency" ? formatCurrency(p.value) : format === "percent" ? formatPercent(p.value) : p.value?.toFixed(1)}
          </span>
        </div>
      ))}
    </div>
  );
}

function TrendCard({ label, current, previous, format }) {
  const change = previous ? ((current - previous) / Math.abs(previous || 1)) * 100 : 0;
  const isPositive = change > 0;
  const isNeutral = Math.abs(change) < 0.5;
  const Icon = isNeutral ? Minus : isPositive ? ArrowUpRight : ArrowDownRight;
  const colorClass = isNeutral ? "text-slate-500 bg-slate-50" : isPositive ? "text-emerald-600 bg-emerald-50" : "text-rose-600 bg-rose-50";

  const formatted = format === "currency" ? formatCurrency(current) : format === "percent" ? formatPercent(current) : current?.toFixed(1);

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3">
        <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-lg font-mono font-bold text-slate-900 mt-1" data-testid={`trend-${label.toLowerCase().replace(/ /g, '-')}`}>{formatted}</p>
        <div className={`inline-flex items-center gap-0.5 mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${colorClass}`}>
          <Icon className="w-3 h-3" />
          {Math.abs(change).toFixed(1)}%
        </div>
      </CardContent>
    </Card>
  );
}

function ChangeIndicator({ current, previous }) {
  if (!previous) return null;
  const change = ((current - previous) / Math.abs(previous || 1)) * 100;
  if (Math.abs(change) < 0.5) return null;
  const isPositive = change > 0;
  return (
    <span className={`ml-1.5 text-[10px] font-semibold ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
      {isPositive ? '+' : ''}{change.toFixed(0)}%
    </span>
  );
}

function QuarterDetailsTable({ historyData, teamId, t, handleExportCSV, handleExportJSON }) {
  const [expandedQ, setExpandedQ] = useState(null);

  const toggleQuarter = (q) => {
    setExpandedQ(prev => prev === q ? null : q);
  };

  return (
    <Card>
      <CardHeader className="card-header-flight-deck flex flex-row items-center justify-between">
        <div>
          <CardTitle>{t("history.quarterDetails")}</CardTitle>
          <CardDescription className="text-xs mt-1">Click any quarter row to view decisions & financial breakdown</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1" data-testid="export-csv-btn">
            <Download className="w-3 h-3" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportJSON} className="gap-1" data-testid="export-json-btn">
            <Download className="w-3 h-3" /> JSON
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]" data-testid="history-table">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-600 w-8"></th>
                <th className="text-left py-3 px-4 font-semibold text-slate-600">{t("history.quarter")}</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-600">{t("dashboard.revenue")}</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-600">{t("history.expenses")}</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-600">{t("dashboard.netProfit")}</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-600">{t("history.cumulative")}</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-600">{t("dashboard.stock")}</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-600">{t("history.load")}</th>
              </tr>
            </thead>
            <tbody>
              {historyData.map((row, index) => {
                const prev = index > 0 ? historyData[index - 1] : null;
                const isExpanded = expandedQ === row.quarter;
                const isRealQuarter = row.quarter > 0;
                return [
                  <tr
                    key={`row-${row.quarter}`}
                    onClick={() => isRealQuarter && toggleQuarter(row.quarter)}
                    className={`border-b border-slate-100 transition-colors ${isRealQuarter ? "cursor-pointer hover:bg-sky-50" : "hover:bg-slate-50"} ${isExpanded ? "bg-sky-50" : ""}`}
                    data-testid={`quarter-row-${row.quarter}`}
                  >
                    <td className="py-3 px-2 text-center">
                      {isRealQuarter && (
                        isExpanded
                          ? <ChevronDown className="w-4 h-4 text-sky-500 inline" />
                          : <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium">Q{row.quarter}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(row.revenue)}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(row.expenses)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`font-mono ${row.net_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(row.net_profit)}
                      </span>
                      {prev && <ChangeIndicator current={row.net_profit} previous={prev.net_profit} />}
                    </td>
                    <td className={`py-3 px-4 text-right font-mono font-semibold ${row.cumulative_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {formatCurrency(row.cumulative_profit)}
                    </td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(row.stock_price, 2)}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatPercent(row.load_factor)}</td>
                  </tr>,
                  isRealQuarter && (
                    <QuarterDetailPanel
                      key={`detail-${row.quarter}`}
                      teamId={teamId}
                      quarter={row.quarter}
                      isOpen={isExpanded}
                      onToggle={() => toggleQuarter(row.quarter)}
                    />
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}



function ComparisonLineChart({ data, currentTeamId, metric, metricLabel, format }) {
  // Build quarter-based data for multi-line chart
  const maxQuarter = Math.max(...data.flatMap(t => t.history.map(h => h.quarter)), 0);
  if (maxQuarter === 0) {
    return <p className="text-center text-slate-500 py-8">No comparison data available yet.</p>;
  }

  const chartData = [];
  for (let q = 1; q <= maxQuarter; q++) {
    const point = { name: `Q${q}` };
    data.forEach(team => {
      const qData = team.history.find(h => h.quarter === q);
      point[team.team_name] = qData ? (qData[metric] || qData.cumulative_profit || 0) : null;
    });
    chartData.push(point);
  }

  return (
    <div className="h-72" data-testid="comparison-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
          <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => format === "currency" ? `$${(v / 1000).toFixed(0)}k` : `${v.toFixed(0)}`} />
          <Tooltip content={<CustomTooltip format={format} />} />
          <Legend />
          <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
          {data.map((team, i) => (
            <Line
              key={team.team_id}
              type="monotone"
              dataKey={team.team_name}
              stroke={TEAM_COLORS[i % TEAM_COLORS.length]}
              strokeWidth={team.team_id === currentTeamId ? 3 : 1.5}
              strokeDasharray={team.is_ai ? "5 5" : undefined}
              dot={{ r: team.team_id === currentTeamId ? 4 : 2 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
